import React, { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper";
import Card from "../../components/Card";
import VideoCard from "../../components/VideoCard";
import ParticipantsTestimonials from "../internship/testimonials-internship/ParticipantTestimonials";
import FAQs1 from "../internship/faqs-internship/FAQs";
import InternshipContactForm from "../homepage/contact/InternshipContactForm";
import { resolveIcon } from "../internship/utils/iconMap";

// Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/autoplay";
import "../internship/InternshipDetailPageDummy.css";

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

function transformCourseData(raw) {
  if (!raw) return null;
  return {
    ...raw,
    // Resolve icon keys in programHighlights features
    programHighlights: raw.programHighlights
      ? {
        ...raw.programHighlights,
        features: (raw.programHighlights.features || []).map((f) => ({
          ...f,
          icon: resolveIcon(f.iconKey),
        })),
      }
      : raw.programHighlights,
    // Resolve icon keys in whoShouldApply
    whoShouldApply: (raw.whoShouldApply || []).map((w) => ({
      ...w,
      icon: resolveIcon(w.iconKey),
    })),
    // imageUrl from toolsCovered maps to the `image` property used by InternshipDetailPage
    toolsCovered: (raw.toolsCovered || []).map((t) => {
      let img = t.imageUrl || null;
      if (img && img.startsWith('/')) {
        img = `${API_URL}${img}`;
      }
      return {
        ...t,
        image: img,
      };
    }),
  };
}

const CourseDetailPreview = ({ courseData: rawCourseData }) => {
  const courseData = transformCourseData(rawCourseData);
  const swiperRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [_isVideoPlaying, setIsVideoPlaying] = useState(false);

  if (!courseData) {
    return <div style={{ padding: "2rem", textAlign: "center" }}>No preview data.</div>;
  }

  // Handle video play/pause to pause/resume Swiper autoplay
  const handleVideoPlayStateChange = (isPlaying, videoId) => {
    if (isPlaying) {
      // Pause all other videos
      const videos = document.querySelectorAll('.alumni-swiper video');
      videos.forEach(video => {
        if (video.dataset.videoId !== videoId) {
          video.pause();
        }
      });

      // Stop Swiper autoplay
      setIsVideoPlaying(true);
      if (swiperRef.current && swiperRef.current.autoplay) {
        swiperRef.current.autoplay.stop();
      }
    } else {
      // Check if any other video is still playing
      const videos = document.querySelectorAll('.alumni-swiper video');
      let anyPlaying = false;
      videos.forEach(video => {
        if (!video.paused) {
          anyPlaying = true;
        }
      });

      if (!anyPlaying) {
        setIsVideoPlaying(false);
        if (swiperRef.current && swiperRef.current.autoplay) {
          swiperRef.current.autoplay.start();
        }
      }
    }
  };

  return (
    <div style={{ overflow: 'hidden', borderRadius: '8px' }}>
      <div
        className="internship-detail-page"
        style={{
          transform: 'scale(0.75)',
          transformOrigin: 'top center',
          width: '133.33%',        /* 1 / 0.75 = 133.33% to compensate for scale */
          marginLeft: '-16.67%',  /* center the scaled element */
          marginBottom: '-25%',   /* pull bottom up to remove empty space from scale */
          background: 'var(--color-bg)',
        }}
      >
        {/* 2. Hero Section */}
        <section className="hero-section" id="header" style={{ padding: '3rem 1rem' }}>
          <div className="container">
            <h1 className="hero-title">{courseData.hero?.headline || "Headline Placeholder"}</h1>
            <center><p className="hero-subtitle">{courseData.hero?.subheadline || "Subheadline Placeholder"}</p></center>
            <p className="hero-description">{courseData.hero?.description || "Description Placeholder"}</p>

            <div className="statistics-grid">
              {(courseData.hero?.statistics || []).map((stat, index) => (
                <Card key={index} className="stat-card">
                  <h3>{stat.value || "0%"}</h3>
                  <p>{stat.label || "Statistic Label"}</p>
                  <p>({stat.ref || "Source"})</p>
                </Card>
              ))}
            </div>

            <div className="hero-cta">
              <a href="#contact" className="btn light" onClick={(e) => e.preventDefault()}>Enroll Now</a>
              <a href="#program-details" className="btn light" onClick={(e) => e.preventDefault()}>Learn More</a>
            </div>
          </div>
        </section>

        {/* 4. Program Highlights */}
        <section className="program-highlights-section" id="highlights">
          <div className="container">
            <h2>Program Highlights</h2>
            <p className="section-subtitle">
              Duration: {courseData.programHighlights?.duration || "3 Months"}
            </p>

            <div className="why-matters-box">
              <h3 className="why-matters-title">
                <span className="arrow-icon">➡️</span> Why This Matters
              </h3>
              <p className="why-matters-intro">
                This is not a traditional internship - it is a structured training
                + internship-style project program with:
              </p>
              <ul className="why-matters-list">
                <li>Real-world portfolio development</li>
                <li>Weekly personalised mentorship</li>
                <li>A Microsoft-partnered certification</li>
                <li>A flexible schedule suitable for full-time professionals</li>
              </ul>
            </div>

            <div className="highlights-grid">
              {(courseData.programHighlights?.features || []).map((feature, index) => (
                <Card key={index} className="highlight-card">
                  <span className="highlight-icon">{feature.icon}</span>
                  <h4>{feature.title || "Feature Title"}</h4>
                  <p>{feature.description || "Feature description goes here."}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* 5. Tools Covered */}
        <section className="tools-section" id="tools">
          <div className="container">
            <h2>Tools & Technologies Covered</h2>
            <p className="section-subtitle">
              Master What Employers Actually Want
            </p>

            <div className="tools-grid">
              {(courseData.toolsCovered || []).map((tool, index) => (
                <Card key={index} className="tool-card light">
                  {tool.image ? (
                    <img src={tool.image} alt={tool.name} />
                  ) : (
                    <div className="tool-card-fallback" aria-hidden="true">
                      {(tool.name || "Tool")
                        .split(' ')
                        .map((part) => part[0])
                        .join('')
                        .slice(0, 3)
                        .toUpperCase()}
                    </div>
                  )}
                  <h4>{tool.name || "Tool Name"}</h4>
                  <p>{tool.description || "Tool description."}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* 6. Why Choose Us */}
        <section className="why-program-section" id="why">
          <div className="container">
            <h2>Why Choose Us?</h2>

            <div className="why-grid">
              {(courseData.whyThisProgram || []).map((reason, index) => (
                <Card key={index} className="why-card">
                  <h4>{reason.title || "Reason Title"}</h4>
                  <p>{reason.description || "Reason description."}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* 7. Program Details */}
        <section className="program-details-section" id="program-details">
          <div className="container">
            <h2>Detailed Program Information</h2>

            <Card className="detail-card">
              <h3>{courseData.programDetails?.overview?.title || "Program Overview"}</h3>
              <p>{courseData.programDetails?.overview?.description || "Description."}</p>
              <div className="phases">
                {(courseData.programDetails?.overview?.phases || []).map((phase, index) => (
                  <div key={index} className="phase">
                    <h4>{phase.title}:</h4>
                    <p>{phase.description}</p>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="detail-card">
              <h3>{courseData.programDetails?.modeOfDelivery?.title || "Mode of Delivery"}</h3>
              <p>{courseData.programDetails?.modeOfDelivery?.description}</p>
            </Card>

            <Card className="detail-card">
              <h3>{courseData.programDetails?.commitment?.title || "Commitment"}</h3>
              <p>{courseData.programDetails?.commitment?.description}</p>
            </Card>

            <Card className="detail-card">
              <h3>{courseData.programDetails?.curriculum?.title || "Curriculum"}</h3>
              <p>{courseData.programDetails?.curriculum?.description}</p>
              <ul>
                {(courseData.programDetails?.curriculum?.points || []).map(
                  (point, index) => (
                    <li key={index}>{point}</li>
                  ),
                )}
              </ul>
            </Card>

            <Card className="detail-card">
              <h3>{courseData.programDetails?.mentorship?.title || "Mentorship"}</h3>
              <p>{courseData.programDetails?.mentorship?.description}</p>
            </Card>

            <Card className="detail-card">
              <h3>{courseData.programDetails?.certification?.title || "Certification"}</h3>
              <p>{courseData.programDetails?.certification?.description}</p>
            </Card>

            <Card className="detail-card">
              <h3>{courseData.programDetails?.careerAdvancement?.title || "Career Advancement"}</h3>
              <p>{courseData.programDetails?.careerAdvancement?.description}</p>
              <p>{courseData.programDetails?.careerAdvancement?.additionalInfo}</p>
              <p className="career-paths">
                {courseData.programDetails?.careerAdvancement?.careerPaths}
              </p>
            </Card>
          </div>
        </section>

        {/* 8. Who Should Apply */}
        <section className="program-highlights-section" id="who-should-apply-details" style={{ background: "transparent" }}>
          <div className="container">
            <h2>Who Should Apply?</h2>
            <div className="who-grid">
              {(courseData.whoShouldApply || []).map((person, index) => (
                <Card key={index} className="who-card">
                  <span className="who-icon">{person.icon}</span>
                  <h4>{person.title || "Applicant Profile"}</h4>
                  <p>{person.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* 9. Alumni Swiper */}
        <section className="who-should-apply-section">
          <div className="container">
            <h2>Voices of Our Program Alumni</h2>

            <Swiper
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              slidesPerView={1}
              spaceBetween={30}
              breakpoints={{}}
              pagination={{ clickable: true }}
              autoplay={{ delay: 4000, disableOnInteraction: true }}
              modules={[Pagination, Autoplay]}
              className="alumni-swiper mySwiper"
            >
              {(courseData.aluminiVideos || []).map((video) => (
                <SwiperSlide key={video.id || video._id}>
                  <article className="testimonial">
                    <VideoCard
                      videoUrl={video.videoUrl}
                      videoId={video.id}
                      onPlayStateChange={handleVideoPlayStateChange}
                    />
                    <div className="testimonial__client-info">
                      <h5>{video.name}</h5>
                      <small>Completed the Program</small>
                    </div>
                  </article>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>

        {/* 10. Footer CTA Section */}
        <section className="cta-section" id="apply">
          <div className="container">
            <Card className="cta-card primary">
              <h2>📨 Ready to Start?</h2>
              <p>
                Don't miss this opportunity to bridge the UK skills gap and launch
                your career! Apply now and take the first step toward a brighter
                future with Int-Elligence Tech Solutions Plc.
              </p>

              <div className="cta-buttons">
                <a href="#contact" className="btn light large" onClick={(e) => e.preventDefault()}>
                  Enroll Now
                </a>
              </div>

              <div className="contact-info">
                <p>For any queries, contact our HR team:</p>
                <p className="email">📧 {courseData.contactEmail || "hr@int-elligence.co.uk"}</p>
              </div>

              <p className="footer-note">{courseData.footerNote}</p>
            </Card>
          </div>
        </section>

        {/* Static components for completeness in preview */}
        <ParticipantsTestimonials isPreview={true} />
        <FAQs1 />

        <section className="additional-info-section">
          <div className="container">
            <Card className="info-card">
              <h3>Additional Information</h3>
              <p>
                This is a learning-focused,{" "}
                <strong>
                  Unpaid Training and Internship - Certificate Program
                </strong>{" "}
                designed to provide hands-on training and experience.
              </p>
            </Card>
          </div>
        </section>

        <InternshipContactForm />
      </div>
    </div>
  );
};

export default CourseDetailPreview;
